// @flow
import { Api } from 'core/services/api'
import { decamelize } from 'utils/camelize'
import type { SocialProvider } from 'core/types'
import type { User } from 'services/user/entities/user'
import AUTH_ROUTES from 'modules/auth/routes-map'
import type {
  RestoreResponse,
  RestoreRequest,
  ResetRequest,
  ResetResponse,
  ChangePasswordParams,
  ChangePasswordResponse,
  RestoreByPhoneRequest,
  RestoreByPhoneResponse,
  SignUpParams,
  SignUpResponse,
  AutoSignUpParams,
  AutoSignUpResponse,
  SignUpWithPhoneParams,
  LoginParams,
} from './types'

/**
 * Restore user password
 * @return {Promise<RestoreRequest>}
 */
export const restorePassword = ({ recaptcha_token, ...values }: RestoreRequest) =>
  Api.post<RestoreRequest, RestoreResponse>('/user/password', { user: decamelize(values), recaptcha_token }).then(
    (response) => response.data
  )

/**
 * Restore user password by phone
 * @return {Promise<RestoreByPhoneResponse>}
 */
export const restorePasswordByPhone = ({ phone, code }: RestoreByPhoneRequest): Promise<RestoreByPhoneResponse> => {
  return Api.post<RestoreByPhoneRequest, RestoreByPhoneResponse>('/api/password_recovery_sms_confirmations', {
    phone,
    code,
  }).then((response) => response.data)
}

/**
 * Reset user password
 * @return {Promise<ResetRequest>}
 */
export const resetPassword = ({ password, resetPasswordToken }: ResetRequest): Promise<ResetResponse> =>
  Api.put<ResetRequest, ResetResponse>('/user/password', {
    user: {
      password,
      reset_password_token: resetPasswordToken,
    },
  }).then((response) => response.data)

/**
 * Change user password
 * @return {Promise<ChangePasswordResponse>}
 */
export const changePassword = (params: ChangePasswordParams): Promise<ChangePasswordResponse> =>
  Api.put<ChangePasswordParams, ChangePasswordResponse>(
    '/api/user/password',
    { user: params },
    { camelize: false }
  ).then((response) => response.data)

/** Delete user's account */
export const deleteAccount = (): Promise<void> => Api.delete<void, void>('/api/user/me').then(() => undefined)

/**
 * Log in
 *
 * @param {object} data:
 *   {string} email
 *   {string} password
 * @return {Promise<void>}
 */
export const logIn = (params: LoginParams): Promise<void> =>
  Api.post<LoginParams, void>(AUTH_ROUTES.LOGIN, {
    user: params,
  }).then(() => undefined)

/** Log out */
export const logOut = async (): Promise<void> => Api.delete('/user/log_out').then(() => undefined)

/** Sign up */
export const signUp = ({ user, provider }: SignUpParams): Promise<SignUpResponse> =>
  Api.post<SignUpParams, SignUpResponse>('/user', { user, provider }).then((response) => response.data)

/** Auto sign up */
export const autoSignUp = (user: User, couponCampaignIds?: number[]): Promise<AutoSignUpResponse> =>
  Api.post<AutoSignUpParams, SignUpResponse>('/api/auto_registration', {
    ...user.getSpyValues(),
    coupon_campaign_ids: couponCampaignIds,
    type: user.type,
  }).then((response) => response.data)

/** Sign up with phone only */
export const signUpWithPhoneOnly = (params: SignUpWithPhoneParams): Promise<AutoSignUpResponse> =>
  Api.post<SignUpWithPhoneParams, SignUpResponse>('/api/onboardings/phone_sign_in', params).then(async (response) => {
    return response
  })

/** Unlink social oauth by provider */
export const unlinkSocial = (provider: SocialProvider) => Api.delete<void, void>(`/api/user/auth/${provider}`)
